import type { MfaRoutingStatus } from '../../hooks/useMfaRouting';

/**
 * Should the chart listener SUBSCRIBE, given the MFA gate decision?
 * (CU-86ak77fuv, B1.)
 *
 * ⚠️ PURE AND SEPARATE FROM THE ROUTE ON PURPOSE. `$patientId.tsx` is bound to a
 * TanStack file route and is not directly mountable in a unit test, so a gate
 * expressed only as an inline boolean there is UNTESTABLE — a mutation that
 * subscribes while gated is caught by nothing but the typechecker. Extracting it
 * makes the security-relevant decision exhaustively testable, the same reasoning
 * that put `decideMfaGate` in `@usacsmideo/types`.
 *
 * ⚠️ `resolving` MUST NOT SUBSCRIBE and must NOT be treated as denied. The gate
 * is fail-closed by design: deciding on an unresolved doc reads
 * `enforced: false` and waves a gated patient straight through. It is also not
 * "withheld" — nothing has been decided yet, so a consumer must keep waiting
 * rather than render the enrol screen.
 */
export function isChartPermitted(status: MfaRoutingStatus): boolean {
  // `exception-bypass` is a REAL pass: an admin-granted, unexpired exception
  // means this patient may see PHI. Omitting it would deny the one cohort with
  // no alternative route (the Trap 5 cohort).
  return status === 'pass' || status === 'exception-bypass';
}

/**
 * Is the chart being withheld BY POLICY — as opposed to still loading?
 *
 * ⚠️ ONLY `enroll`. `resolving` is "not decided yet", which is genuinely still
 * loading; reporting it as withheld would let the gates below fall through
 * before the decision exists, which is the fail-open direction.
 */
export function isChartWithheld(status: MfaRoutingStatus): boolean {
  return status === 'enroll';
}
